import { useGetInvestorHome } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { Building2, TrendingUp, Calendar, ChevronRight } from "lucide-react";
import InfoTip from "@/components/info-tip";

export default function InvestorHome({ investorId, cohortSlug }: { investorId: number; cohortSlug?: string }) {
  const { data, isLoading, error } = useGetInvestorHome(investorId);

  if (isLoading) return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-64" />
      <div className="grid gap-4">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32" />)}
      </div>
    </div>
  );

  if (error || !data) return (
    <div className="p-8 text-center text-muted-foreground">Unable to load investor dashboard</div>
  );

  const { investor, accessibleCompanies } = data;

  return (
    <div className="space-y-8" data-testid="page-investor-home">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-serif font-bold tracking-tight">Welcome, {investor.name.split(" ")[0]}</h1>
          <InfoTip content="This is your Investor Intelligence Room — a curated view of every Favcy portfolio company you have access to. Each company has its own microsite with approved updates, traction metrics, team bios, and key documents. Only founder-approved information appears here." />
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {investor.firm ? `${investor.firm} — ` : ""}Your portfolio intelligence at a glance
        </p>
      </div>

      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-serif font-bold">{accessibleCompanies?.length || 0}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1">Accessible Companies <InfoTip content="The number of Favcy portfolio companies that have been shared with you. Each company grants either 'standard' or 'detailed' access. Detailed access includes full KPI data and documents." /></p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div>
        <h2 className="text-lg font-serif font-bold mb-4">Your Portfolio</h2>
        {!accessibleCompanies?.length ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              No companies are currently shared with you. Contact Favcy for access.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {accessibleCompanies.map((entry: any) => (
              <Link key={entry.company.id} href={cohortSlug ? `/c/${cohortSlug}/investor/${investorId}/company/${entry.company.id}` : `/investor/${investorId}/company/${entry.company.id}`}>
                <Card className="cursor-pointer hover:shadow-md transition-shadow group">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-muted-foreground" />
                        </div>
                        <div>
                          <h3 className="font-medium text-sm">{entry.company.name}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {entry.company.sector} &middot; {entry.company.stage}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge variant="outline" className="text-[10px]">
                              {entry.accessLevel} access
                            </Badge>
                            {entry.latestUpdateDate && (
                              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Updated {new Date(entry.latestUpdateDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
